import { useState, useEffect } from 'react';
import { knowledgeBaseService, type CreateKnowledgeBaseRequest, type KnowledgeBase } from '../services/knowledgeBaseService';
import { projectService, type Project } from '../services/projectService';
import { subProjectService } from '../services/subProjectService';
import { useAuth } from '../hooks/useAuth';
import './CreateKBModal.css';

interface SubProject {
  id: string;
  name: string;
  project_id: string;
}

interface CreateKBModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess?: () => void;
  onCreated?: (kb: KnowledgeBase) => void;
  prefilledProjectId?: string | null;
  prefilledSubProjectId?: string | null;
  prefilledName?: string;
  context?: string;
}

export function CreateKBModal({
  isOpen,
  onClose,
  onSuccess,
  onCreated,
  prefilledProjectId = null,
  prefilledSubProjectId = null,
  prefilledName = '',
  context,
}: CreateKBModalProps) {
  const { user } = useAuth();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [projects, setProjects] = useState<Project[]>([]);
  const [subProjects, setSubProjects] = useState<SubProject[]>([]);
  const [formData, setFormData] = useState<CreateKnowledgeBaseRequest>({
    name: prefilledName,
    owner_id: user?.id || '',
    project_id: prefilledProjectId,
    sub_project_id: prefilledSubProjectId,
    description: null,
  });

  // Load projects and sub-projects when modal opens
  useEffect(() => {
    if (isOpen) {
      loadProjectsAndSubProjects();
    }
  }, [isOpen]);

  // Update form when prefilled values change
  useEffect(() => {
    if (isOpen) {
      setFormData(prev => ({
        ...prev,
        name: prefilledName || prev.name,
        project_id: prefilledProjectId,
        sub_project_id: prefilledSubProjectId,
      }));
    }
  }, [isOpen, prefilledProjectId, prefilledSubProjectId, prefilledName]);

  const loadProjectsAndSubProjects = async () => {
    try {
      const [projectsData, subProjectsData] = await Promise.all([
        projectService.getProjects(),
        subProjectService.getAll(),
      ]);
      setProjects(projectsData);
      setSubProjects(subProjectsData);
    } catch (error) {
      console.error('Error loading projects:', error);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!user?.id) {
      alert('User not authenticated');
      return;
    }

    try {
      setIsSubmitting(true);
      const createData: CreateKnowledgeBaseRequest = {
        name: formData.name,
        owner_id: user.id,
        project_id: formData.project_id || undefined,
        sub_project_id: formData.sub_project_id || undefined,
        description: formData.description || undefined,
        context: context || undefined,
      };
      
      const newKb = await knowledgeBaseService.create(createData);
      
      // Reset form
      setFormData({
        name: prefilledName,
        owner_id: user.id,
        project_id: prefilledProjectId,
        sub_project_id: prefilledSubProjectId,
        description: null,
      });
      
      onClose();
      if (onCreated) {
        onCreated(newKb);
      }
      if (onSuccess) {
        onSuccess();
      }
    } catch (err: unknown) {
      console.error('Error creating KB:', err);
      // Extract detailed error message from axios error response
      let errorMessage = 'Failed to create knowledge base';
      if (err && typeof err === 'object') {
        const axiosError = err as { response?: { status?: number; data?: { detail?: string } }; message?: string };
        if (axiosError.response?.data?.detail) {
          errorMessage = `Error ${axiosError.response.status}: ${axiosError.response.data.detail}`;
        } else if (axiosError.message) {
          errorMessage = axiosError.message;
        }
      }
      alert(errorMessage);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    if (!isSubmitting) {
      setFormData({
        name: prefilledName,
        owner_id: user?.id || '',
        project_id: prefilledProjectId,
        sub_project_id: prefilledSubProjectId,
        description: null,
      });
      onClose();
    }
  };

  const filteredSubProjects = formData.project_id
    ? subProjects.filter(sp => sp.project_id === formData.project_id)
    : [];

  if (!isOpen) return null;

  const isProjectLocked = prefilledProjectId !== null;
  const isSubProjectLocked = prefilledSubProjectId !== null;

  return (
    <div className="create-kb-modal-overlay" onClick={handleClose}>
      <div className="create-kb-modal-content" onClick={(e) => e.stopPropagation()}>
        <div className="create-kb-modal-header">
          <h2>Create Knowledge Base</h2>
          <button className="create-kb-close-btn" onClick={handleClose} disabled={isSubmitting}>
            ×
          </button>
        </div>

        <form onSubmit={handleSubmit}>
          <div className="create-kb-modal-body">
            <div className="create-kb-form-group">
              <label htmlFor="kb-name">Name *</label>
              <input
                id="kb-name"
                type="text"
                required
                maxLength={200}
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                placeholder="e.g., Technical Documents, Patent Analysis"
                autoFocus
              />
            </div>

            <div className="create-kb-form-group">
              <label htmlFor="kb-owner">Owner</label>
              <input
                id="kb-owner"
                type="text"
                value={user ? `${user.first_name} ${user.family_name}` : ''}
                disabled
              />
            </div>

            <div className="create-kb-form-group">
              <label htmlFor="kb-project">
                Project {isProjectLocked && <span className="locked-badge">🔒 Linked</span>}
              </label>
              <select
                id="kb-project"
                value={formData.project_id ?? ''}
                onChange={(e) =>
                  setFormData({
                    ...formData,
                    project_id: e.target.value || null,
                    sub_project_id: null,
                  })
                }
                disabled={isProjectLocked}
              >
                <option value="">Select Project (Optional)</option>
                {projects.map((project) => (
                  <option key={project.id} value={project.id}>
                    {project.name}
                  </option>
                ))}
              </select>
              {isProjectLocked && (
                <small className="field-note">
                  This KB will be linked to the current project
                </small>
              )}
            </div>

            <div className="create-kb-form-group">
              <label htmlFor="kb-sub-project">
                Sub-Project {isSubProjectLocked && <span className="locked-badge">🔒 Linked</span>}
              </label>
              <select
                id="kb-sub-project"
                value={formData.sub_project_id ?? ''}
                onChange={(e) => setFormData({ ...formData, sub_project_id: e.target.value || null })}
                disabled={isSubProjectLocked || !formData.project_id}
              >
                <option value="">Select Sub-Project (Optional)</option>
                {filteredSubProjects.map((sp) => (
                  <option key={sp.id} value={sp.id}>
                    {sp.name}
                  </option>
                ))}
              </select>
              {isSubProjectLocked && (
                <small className="field-note">
                  This KB will be linked to the current sub-project
                </small>
              )}
              {!isSubProjectLocked && !formData.project_id && (
                <small className="field-note">
                  Select a project first to choose a sub-project
                </small>
              )}
            </div>

            <div className="create-kb-form-group">
              <label htmlFor="kb-description">Description</label>
              <textarea
                id="kb-description"
                maxLength={1000}
                rows={3}
                value={formData.description || ''}
                onChange={(e) => setFormData({ ...formData, description: e.target.value || null })}
                placeholder="Describe the purpose of this knowledge base..."
              />
            </div>
          </div>

          <div className="create-kb-modal-footer">
            <button
              type="button"
              className="create-kb-btn create-kb-btn-secondary"
              onClick={handleClose}
              disabled={isSubmitting}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="create-kb-btn create-kb-btn-primary"
              disabled={isSubmitting || !formData.name.trim()}
            >
              {isSubmitting ? 'Creating...' : 'Create Knowledge Base'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
